import funnelStep1 from "@/assets/funnel-step-1.svg";
import funnelStep2 from "@/assets/funnel-step-2.svg";
import funnelStep3 from "@/assets/funnel-step-3.svg";
import logo from "@/assets/hiredcoach-logo.svg";
import LandingFeaturesMain from "@/components/marketing/LandingFeaturesMain";
import LandingHowItWorks from "@/components/marketing/LandingHowItWorks";
import LandingPricing from "@/components/marketing/LandingPricing";
import Seo from "@/components/marketing/Seo";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowRight, CheckCircle2, ChevronDown } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

type FunnelJourneyStep = {
  id: string;
  stepLabel: string;
  title: ReactNode;
  bullets: ReactNode[];
  illustration: string;
  illustrationAlt: string;
};

const funnelJourneySteps: FunnelJourneyStep[] = [
  {
    id: "organize",
    stepLabel: "The Hunt",
    title: (
      <>
        Keep your search <span className="text-[#4f9ecb]">ORGANIZED</span>
      </>
    ),
    bullets: [
      "Track applications, follow-ups, and moving pieces in one place.",
      "Keep momentum without building your own spreadsheet monster.",
      <>
        <span className="text-[#4f9ecb] font-semibold">Hired</span>
        <span className="text-[#334763] font-semibold">Coach</span> helps you stay on top of the early chaos.
      </>,
    ],
    illustration: funnelStep1,
    illustrationAlt: "Job seeker organizing applications and networking",
  },
  {
    id: "interview",
    stepLabel: "The Interview",
    title: (
      <>
        Tell your story <span className="text-[#4f9ecb]">CONFIDENTLY</span>
      </>
    ),
    bullets: [
      "Practice with role-aware interview questions instead of generic fluff.",
      "Get better at saying what you did, why it mattered, and why they should care.",
      <>
        <span className="text-[#4f9ecb] font-semibold">Hired</span>
        <span className="text-[#334763] font-semibold">Coach</span> helps you walk in sharper and calmer.
      </>,
    ],
    illustration: funnelStep2,
    illustrationAlt: "Preparing confidently for a job interview",
  },
  {
    id: "offer",
    stepLabel: "The Offer",
    title: (
      <>
        Handle the money talk with <span className="text-[#4f9ecb]">CLARITY</span>
      </>
    ),
    bullets: [
      "Rehearse compensation conversations before the real one happens.",
      "Frame your value without freezing up or underselling yourself.",
      <>
        Earn what you <span className="text-[#4f9ecb] font-semibold">DESERVE</span>, not whatever they can get you to accept.
      </>,
    ],
    illustration: funnelStep3,
    illustrationAlt: "Discussing salary and offer terms",
  },
];

const structuredData = [
  {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "HiredCoach",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    description:
      "HiredCoach helps job seekers organize their search, practice interviews, improve their pitch, and prepare for salary negotiations.",
    offers: {
      "@type": "AggregateOffer",
      lowPrice: "4.99",
      highPrice: "49.99",
      priceCurrency: "USD",
      offerCount: 3,
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What does HiredCoach help with?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "HiredCoach helps job seekers organize their search, practice interviews, refine their pitch, and prepare for salary negotiations.",
        },
      },
      {
        "@type": "Question",
        name: "Who is HiredCoach for?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "HiredCoach is for active job seekers who want stronger preparation from the first application through interview and offer stages.",
        },
      },
    ],
  },
];

const Index = () => {
  const { user } = useAuth();
  const homeTarget = user ? "/dashboard" : "/";
  const learnTarget = user ? "/learn" : "/public-learn";

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="HiredCoach | Interview Prep, Job Search Organization, and Salary Negotiation Coaching"
        description="HiredCoach helps job seekers organize their search, practice interviews, refine their pitch, and prepare for salary negotiations."
        canonicalPath="/"
        keywords="interview prep, salary negotiation coaching, job search organization, resume analysis, career coaching"
        structuredData={structuredData}
      />

      <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="container mx-auto flex items-center justify-between px-4 py-4 sm:px-6">
          <Link to={homeTarget} className="flex items-center gap-2">
            <img src={logo} alt="HiredCoach Logo" className="h-10 w-10" />
            <span className="text-xl font-bold">
              <span className="text-[#4f9ecb]">Hired</span>
              <span className="text-[#334763]">Coach</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-6 text-sm font-medium text-slate-600 md:flex">
            <a href="#features" className="hover:text-slate-900">Features</a>
            <a href="#pricing" className="hover:text-slate-900">Pricing</a>
            <Link to={learnTarget} className="hover:text-slate-900">Learn</Link>
            {/* <a href="#how-it-works" className="hover:text-slate-900">How It Works</a> */}
          </nav>

          <div className="flex items-center gap-2">
            {user ? (
              <Button className="bg-sky-600 text-white hover:bg-sky-500" asChild>
                <Link to="/dashboard">Dashboard</Link>
              </Button>
            ) : (
              <>
                <Button variant="ghost" className="bg-slate-100 text-slate-700 hover:bg-slate-200" asChild>
                  <Link to="/login">Sign In</Link>
                </Button>
                <Button className="bg-sky-600 text-white hover:bg-sky-500" asChild>
                  <Link to="/register">Sign Up</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      <section className="overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(79,158,203,0.28),_transparent_38%),linear-gradient(180deg,#f8fbff_0%,#ffffff_62%)]">
        <div className="container mx-auto px-6 py-8 md:py-16">
          <div className="grid items-center gap-14 lg:grid-cols-[1.15fr_0.85fr]">
            <div className="w-full max-lg:mx-auto max-lg:max-w-3xl">
              <div className="lg:hidden">
                <div className="flex flex-col items-center justify-center gap-3 md:flex-row md:items-center md:justify-start md:gap-6">
                  <img
                    src={logo}
                    alt="HiredCoach Logo"
                    className="order-1 h-48 w-48 md:order-2 md:h-24 md:w-24"
                  />
                  <p className="order-2 text-center text-sm font-semibold uppercase leading-normal tracking-[0.28em] md:order-1 md:text-left">
                    <span className="text-[#4f9ecb]">Hired</span>
                    <span className="text-[#334763]">Coach</span> has your back
                  </p>
                </div>
              </div>
              <p className="hidden text-sm font-semibold uppercase leading-normal tracking-[0.28em] lg:block">
                <span className="text-[#4f9ecb]">Hired</span>
                <span className="text-[#334763]">Coach</span> has your back
              </p>
              <h1 className="mt-6 text-center text-4xl/[1.15] font-bold tracking-tight text-[#334763] md:text-left md:text-5xl/[1.12]">
                Job hunting <span className="text-[#4f9ecb]">f@#&ing</span> sucks
              </h1>
              <h2 className="mt-4 text-center text-4xl/[1.15] font-bold tracking-tight text-[#334763] md:text-left md:text-5xl/[1.12]">
                The <span className="text-[#4f9ecb]">Coach</span> is here to make it a little less brutal
              </h2>
              <p className="mt-6 text-center text-lg leading-relaxed text-slate-600 md:text-left">
                It can take <span className="text-[#4f9ecb] font-bold">100 applications</span> to get just <span className="text-[#4f9ecb] font-bold">1 interview</span>. When that interview lands, you should be ready for it.
              </p>
              <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center md:justify-start">
                <Button className="h-16 rounded-full bg-sky-600 px-10 text-lg font-semibold text-white shadow-xl shadow-sky-200 transition hover:bg-sky-500" asChild>
                  <Link to="/register">
                    Sounds great - let's go!
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </div>
            </div>

            <div className="relative hidden items-center justify-center self-center lg:flex">
              <div className="absolute -left-10 top-10 h-28 w-28 rounded-full bg-sky-200/60 blur-3xl" />
              <div className="absolute bottom-0 right-0 h-36 w-36 rounded-full bg-amber-200/60 blur-3xl" />
              <img
                src={logo}
                alt="HiredCoach Logo"
                className="relative z-[1] h-auto w-full max-w-[min(100%,24rem)] object-contain xl:max-w-[min(100%,30rem)]"
              />
            </div>
          </div>
        </div>
      </section>

      <LandingFeaturesMain />

      <section className="bg-white py-10 md:py-14" aria-labelledby="funnel-journey-heading">
        <div className="container mx-auto px-6">
          <div className="mx-auto max-w-3xl text-center">
            <h2 id="funnel-journey-heading" className="text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">
              Job hunting, step by step
            </h2>
            <p className="mt-4 text-lg font-medium text-slate-800 md:text-xl">
              <span className="text-[#4f9ecb]">Hired</span><span className="text-[#334763]">Coach</span> helps at every stage.
            </p>
          </div>

          <div className="mx-auto mt-10 max-w-6xl">
            <div className="flex flex-col lg:flex-row lg:items-stretch lg:gap-3">
              {funnelJourneySteps.map((stage, index) => {
                const isLast = index === funnelJourneySteps.length - 1;

                return (
                  <div key={stage.id} className="contents">
                    <div className="flex flex-1 flex-col">
                      <Card className="relative flex h-full flex-col rounded-3xl border border-slate-200 bg-white shadow-sm">
                        <CardContent className="flex flex-1 flex-col items-center p-6 md:p-7">
                          <p className="w-full text-center text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                            {stage.stepLabel}
                          </p>
                          <div className="mt-3 flex w-full max-w-[16rem] items-center justify-center min-h-[8rem] sm:min-h-[10rem]">
                            <img
                              src={stage.illustration}
                              alt={stage.illustrationAlt}
                              className="h-auto w-full max-h-[10rem] object-contain object-center sm:max-h-[12rem]"
                              loading="lazy"
                              decoding="async"
                            />
                          </div>
                          <div className="mx-auto mt-4 w-full max-w-md">
                            <h3 className="text-center text-xl font-bold leading-snug text-slate-900 md:text-2xl">
                              {stage.title}
                            </h3>
                            <ul className="mt-4 space-y-2 text-left text-sm text-slate-700">
                              {stage.bullets.map((bullet, bulletIndex) => (
                                <li key={`${stage.id}-${bulletIndex}`} className="flex gap-2 leading-6">
                                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" aria-hidden />
                                  <span>{bullet}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {!isLast && (
                      <>
                        <div className="flex flex-col items-center py-2 lg:hidden" aria-hidden>
                          <div className="h-8 w-px bg-gradient-to-b from-slate-200 via-slate-300 to-slate-200" />
                          <ChevronDown className="-mt-1 h-5 w-5 text-slate-400" />
                        </div>
                        <div className="hidden w-10 shrink-0 items-center justify-center lg:flex" aria-hidden>
                          <div className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-400">
                            <ArrowRight className="h-4 w-4" />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-10 flex flex-col items-center gap-3 text-center">
              <Button className="h-14 rounded-full bg-sky-600 px-8 text-base font-semibold text-white shadow-lg shadow-sky-200 hover:bg-sky-500" asChild>
                <Link to="/register">
                  I want to interview confidently
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <LandingPricing
        eyebrow="Pricing"
        title="Free to try, unlimited with premium"
        description=""
        sectionId="pricing"
        theme="dark"
      />
{/* 
      <div id="how-it-works">
        <LandingHowItWorks />
      </div> */}

      <footer className="bg-slate-950 px-6 py-14 text-white">
        <div className="container mx-auto">
          <div className="grid gap-12 md:grid-cols-4">
            <div>
              <div className="flex items-center gap-3">
                <img src={logo} alt="HiredCoach Logo" className="h-12 w-12" />
                <span className="text-xl font-bold">
                  <span className="text-[#4f9ecb]">Hired</span>
                  <span className="text-white">Coach</span>
                </span>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold uppercase tracking-[0.24em] text-slate-500">Explore</h3>
              <div className="mt-4 space-y-3 text-sm text-slate-400">
                <a href="#features" className="block hover:text-white">Features</a>
                <a href="#pricing" className="block hover:text-white">Pricing</a>
                {/* <a href="#how-it-works" className="block hover:text-white">How It Works</a> */}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold uppercase tracking-[0.24em] text-slate-500">Resources</h3>
              <div className="mt-4 space-y-3 text-sm text-slate-400">
                <Link to="/public-learn" className="block hover:text-white">CoachU</Link>
                <Link to="/agreements" className="block hover:text-white">Terms and Policies</Link>
                <Link to="/contact" className="block hover:text-white">Contact</Link>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold uppercase tracking-[0.24em] text-slate-500">Account</h3>
              <div className="mt-4 space-y-3 text-sm text-slate-400">
                <Link to="/register" className="block hover:text-white">Create Account</Link>
                <Link to="/login" className="block hover:text-white">Sign In</Link>
              </div>
            </div>
          </div>

          <div className="mt-12 border-t border-slate-800 pt-8 text-center text-sm text-slate-500">
            &copy; {new Date().getFullYear()} HiredCoach. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
